import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {Whitelabel} from './model';
import {clone} from 'lodash';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import {catchError, map, tap} from 'rxjs/operators';
import {DOCUMENT} from '@angular/common';


const WHITELABELS: Map<string, Whitelabel> = new Map([
    ["default",
        {
            id: "quansic",
            poweredByQuansic: false,
            logo: "luminate/luminate-logo.svg",
        }],

    ["bridger",
        {
            id: "bridger",
            poweredByQuansic: true,
            name: "bridger/bridger.png"
        }],

    ["vevasound",
        {
            id: "vevasound",
            poweredByQuansic: true,
            logo: "veva/veva.png",
            name: "veva/veva_name_dark.png",
            confirmation: {
                isni: "fairchild@vevasound.com"
            }
        }],
    ["opusmusic",
        {
            id: "opusmusic",
            poweredByQuansic: true,
            logo: "opusmusic/OpusMusicLogo.png",
        }],
    ["lorelailab",
    {
        id: "lorelailab",
        poweredByQuansic: true,
        logo: "lorelailab/lorelailab.png",
        confirmation: {
            isni: "aurelie@lorelai-lab.com"
        }
    }],
    ["metamusique",
    {
        id: "metamusique",
        poweredByQuansic: true,
        logo: "metamusic/metamusic.png",
        confirmation: {
            isni: "aurelie@quansic.com"
        }
    }],
    ["warner",
    {
        id: "warner",
        poweredByQuansic: true,
        logo: "warner/warner_logo.gif",
        confirmation: {
            isni: "nick.bernstein@wmg.com"
        }
    }]
]);



@Injectable({
    providedIn: 'root'
})
export class WhiteLabelService {
    private origin = ""
    private whiteLabels: Map<string, Whitelabel> = new Map(WHITELABELS);

    whitelabels$: Subject<any>;
    whitelabel:Whitelabel|undefined;

    /**
     * Email of the current whitelabel, used to prefill the "Registered by" field.
     * Held in memory only: it is fetched from the BFF when a whitelabel path is loaded.
     */
    private registrantEmail: string|undefined;

    constructor(private http:HttpClient, @Inject(DOCUMENT) private document: Document) {
        this.origin = `${document.location.origin}`;
        this.whitelabels$ = new BehaviorSubject(this.default());
        this.restoreDefault();
    }

    default(): Whitelabel|undefined {
        return clone(this.whiteLabels.get("default"));
    }

    applyWhiteLabel(whitelabelId: string) {
        const wl: Whitelabel|undefined = this.whiteLabels.get(whitelabelId);
        if(typeof wl !== "undefined"){
            this.whitelabel = wl;
            this.whitelabels$.next(clone(wl));
        }
    }

    restoreDefault(): void {
        this.applyWhiteLabel("default");
    }

    /**
     * Called when a whitelabel path is loaded. Asks the BFF for the email to prefill the
     * registrant field with, then applies the whitelabel branding.
     *
     * Authentication is no longer involved: the application authenticates itself against
     * the Luminate APIs, so a whitelabel is only branding plus a prefilled email. A
     * failure here must not block the page, since both are optional.
     */
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {
        const wlId:string = route.data.id as string;
        return this.http.get(`${this.origin}/api/whitelabel/id/${wlId}`).pipe(
            tap((result:any) => { this.registrantEmail = result?.email; }),
            catchError(() => of(null)),
            tap(() => this.applyWhiteLabel(wlId)),
            map(() => true)
        )
    }

    /** Email to prefill the registrant field with, when the form was loaded through a whitelabel path. */
    getRegistrantEmail(): string|undefined {
        return this.registrantEmail;
    }

    /** Id reported to the activity logs, or undefined outside of a whitelabel path. */
    getWhitelabelId(): string|undefined {
        const id = this.whitelabel?.id;
        return (id === undefined || id === this.whiteLabels.get("default")?.id) ? undefined : id;
    }

    isUserVevaCollect() {
      return this.getWhitelabelId() === "vevasound";
    }

    isUserBridger() {
        return this.getWhitelabelId() === "bridger";
    }

    isUserOpusMusic() {
        return this.getWhitelabelId() === "opusmusic";
    }

    isUserLorelaiLab() {
        return this.getWhitelabelId() === "lorelailab";
    }

    isUserMetaMusique() {
        return this.getWhitelabelId() === "metamusique";
    }

    isUserWarnerMusic() {
        return this.getWhitelabelId() === "warner";
    }
}
